import { useInspectorMode } from "@utils/ContentfulUtils";
import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { getCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import React, { FC, useContext, useId, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking, ctaClickTrackingWithProjectName, pushButtonClick, testimonialsCardClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import useMetaverseRouteChangeHandler from "@/hooks/useMetaverse";
import { CustomLink, VideoModal } from "@/types";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

interface Props {
  cta: CustomLink | VideoModal | string | any;
  ctaType?: string;
  btnClasses?: string;
  btnSize?: ButtonSize;
  showIcon?: boolean;
  iconBlack?: boolean;
  modalTitle?: string;
  isInventoryAvaialable?: boolean;
  style?: any;
  isMetaverseCta?: boolean;
  title?: string;
  componentName?: string;
}

const isVideoModal = (cta: CustomLink | VideoModal | any): cta is VideoModal => cta?.type === "videoModal";

const ButtonOrVideoModal: FC<Props> = ({
  cta,
  btnClasses,
  showIcon,
  btnSize,
  iconBlack,
  modalTitle,
  isInventoryAvaialable,
  style,
  isMetaverseCta = false,
  title,
  componentName,
}) => {
  const videoID = useId();
  const t = useTranslations();
  const locale = useLocale();
  const { setIsVideoModalOpenIframe } = useContext(UserContext);
  const { data: session } = useSession();
  const [modalOpen, setModalOpen] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const { setEnquireModalOpen } = useContext(ModalsContext);

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const currency = getCookie("currency") || "AED";
  useMetaverseRouteChangeHandler(isIframeLoaded);
  const [language, region] = locale.split("-");

  function getVideoUrlWithLangAndCurrency(cta: any, language: string, currency: string): string {
    const url = cta?.embeddedVideo?.link?.url;
    if (url.includes("www.youtube.com")) {
      return url;
    }

    // if (url?.includes("view.propvr")) {
    //   // const showUnit = isInventoryAvaialable ? 1 : 0;
    //   console.log("cat", cta);
    //   cta?.projectData?.status.toLowerCase() === "reveal" && localStorage.setItem("eoiPId", cta?.projectData?.id || "");
    //   const eoi = cta?.projectData?.status.toLowerCase() === "reveal" ? 1 : 0;
    //   // return `${url}?showUnit=${showUnit}&eoiCta=${eoiCta}`;
    //   return `${url}?eoi=${eoi}`;
    // }
    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.split("/");
      if (pathParts[1] === "ar" || pathParts[1] === "en" || pathParts[1] === "ru") {
        pathParts[1] = language || "en";
      } else {
        pathParts.splice(1, 0, language || "en");
      }
      parsedUrl.pathname = pathParts.join("/");
      parsedUrl.searchParams.set("currency", currency?.toLowerCase() || "aed");
      return parsedUrl?.href;

      // const finalUrl = new URL(parsedUrl.href);

      // if (finalUrl.href.includes("view.propvr")) {
      //   const status = cta?.projectData?.cmsProjectStatus?.toLowerCase();
      //   status === "reveal" && localStorage.setItem("eoiPId", cta?.projectData?.id);
      //   localStorage.setItem(
      //     "eoiPId",
      //     JSON.stringify({ id: cta?.projectData?.id, slug: cta?.projectData?.slug, projectName: cta?.projectData?.projectName }),
      //   );
      //   const eoi = status === "reveal" ? 1 : 0;
      //   finalUrl.searchParams.set("eoi", String(eoi));
      // }
      // return finalUrl.href;
    } catch {
      return url;
    }
  }

  const pathname = usePathname();
  const projectSlug = pathname?.split("/")?.find((segment, idx, arr) => arr[idx - 1] === "projects" || arr[idx - 1] === "communities");

  // Tile Carousel Datalayer
  const sessionStorageForDatalayer = {
    clicked: true,
    section: { title: title },
  };

  const isHomePage = pathname === "/" || pathname === `/${locale}/`;

  return (
    <>
      <Button
        {...(isVideoModal(cta)
          ? {
              onClick: () => {
                setModalOpen(true);
                // ctaClickTrackingWithProjectName(
                //   cta?.internalName || cta?.buttonLabel,
                //   "ButtonOrVideoModal",
                //   cta?.internalName || cta.buttonLabel,
                //   projectSlug || "",
                //   cta?.internalName || "",
                //   isMetaverseCta,
                // );
                ctaClickTrackingWithProjectName(
                  cta.buttonLabel,
                  "ButtonOrVideoModal",
                  cta.buttonLabel,
                  projectSlug || "",
                  isMetaverseCta,
                  cta?.ctaType || "",
                );
              },
            }
          : {
              onClick: () => {
                pushButtonClick(cta?.label, "ButtonOrVideoModal", !!session);
                testimonialsCardClick({
                  tile_carousel_name: componentName ?? "",
                  tile_card_name: title ?? "",
                  tile_card_location: isHomePage ? "Home Page" : "",
                });
                sessionStorage.setItem("tileCarousel", JSON.stringify(sessionStorageForDatalayer));
              },
              href: getLink(cta, locale),
            })}
        size={btnSize}
        customClass={btnClasses}
        {...(style ? { style } : {})}
      >
        <span className="flex items-center gap-xs">
          {showIcon && <Icon className={cn(iconBlack ? "stroke-black-0" : "stroke-white")} name="play" size="16" title="play" />}
          {isVideoModal(cta) ? cta.buttonLabel : cta?.label}
        </span>
      </Button>
      {isVideoModal(cta) && (
        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setIsVideoModalOpenIframe(false);
          }}
        >
          <ModalContent className="h-dvh w-dvw !rounded-none pt-0 md:pt-m !px-0 md:!px-l">
            <div className={cn("flex items-center justify-between mb-l px-m md:px-0 pt-l")}>
              <p className={cn("font-primary uppercase")}>{modalTitle ?? cta.buttonLabel}</p>
              <Button
                className={"stroke-black"}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => {
                  setModalOpen(false);
                  setIsVideoModalOpenIframe(false);
                }}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title={t("label.common.close")} />
                  <span className="sr-only">{t("button.label.closeModal")}</span>
                </span>
              </Button>
            </div>
            {cta.embeddedVideo ? (
              <iframe
                className={cn("object-cover w-full h-[calc(100dvh-72px)] md:rounded-lg border-0")}
                width="560"
                height="315"
                src={
                  cta?.embeddedVideo?.link?.url?.includes("view.propvr")
                    ? getVideoUrlWithLangAndCurrency(cta, language, currency)
                    : cta.embeddedVideo.link.url
                }
                onLoad={() => {
                  setIsIframeLoaded(true);
                  setIsVideoModalOpenIframe(true); // Show chat when iframe loads
                }}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            ) : (
              <div className="relative w-full h-[calc(100dvh-72px)]">
                <video
                  id={videoID}
                  ref={videoRef}
                  className={cn("object-cover w-full h-[calc(100dvh-72px)] max-h-[calc(100dvh-72px)] md:rounded-lg")}
                  // src={isDesktop ? cta.video?.url : cta.mobileVideo?.url || cta.video?.url}
                  controls
                  autoPlay
                  // loop
                  playsInline
                  muted
                  onEnded={() => setShowButton(true)}
                >
                  <source src={isDesktop ? cta.video?.url : cta.mobileVideo?.url || cta.video?.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {showButton && (
                  <>
                    <div className="absolute inset-0 bg-black z-10 opacity-80" />
                    <div className="absolute inset-0 flex items-center justify-center z-20 gap-l">
                      {!isInventoryAvaialable ? (
                        <Button
                          size={ButtonSize.MEDIUM}
                          variant={ButtonVariant.PRIMARY}
                          theme={ButtonTheme.WHITE}
                          onClick={() => {
                            setModalOpen(false);
                            setEnquireModalOpen(true);
                            setShowButton(false);
                          }}
                        >
                          {t("stickybar.desktop.enquire")}
                        </Button>
                      ) : (
                        <Button
                          size={ButtonSize.MEDIUM}
                          variant={ButtonVariant.PRIMARY}
                          theme={ButtonTheme.WHITE}
                          onClick={() => {
                            setTimeout(() => {
                              const AULComponent = document.body.querySelector("#APL-component");
                              if (AULComponent) {
                                const rect = AULComponent.getBoundingClientRect();
                                window.scrollTo({
                                  top: rect.top + window.scrollY,
                                  behavior: "smooth",
                                });
                              }
                            }, 500);
                            setModalOpen(false);
                            setShowButton(false);
                          }}
                        >
                          {t("stickybar.desktop.selectUnit")}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ButtonOrVideoModal;
